// fe/src/modules/tenant/CommercialManagerDashboard.tsx
// Financial & Billing page: current plan, usage against limits, alerts, invoices.
// Includes a demo scenario switcher that previews usage levels without touching real data.

import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, CreditCard, Download, HardDrive, Info, RefreshCw, RotateCcw, Users, XCircle } from "lucide-react";
import PaymentModal from "../billing/PaymentModal";
import UpgradeRequestModal from "../billing/UpgradeRequestModal";
import {
    SCENARIOS,
    WARN_PCT,
    applyScenario,
    formatBytes,
    formatDate,
    formatMoney,
    getActiveScenario,
    openInvoicePrintView,
    resetDemoPayment,
    scenarioLabel,
    setActiveScenario,
    usageLevel,
    usePlan,
    type PlanResponse,
    type PlanUsage,
    type ScenarioId,
    type UsageLevel,
} from "../billing/planApi";

interface CommercialManagerProps {
    tenantId: string;
}

// ---- alerts -------------------------------------------------------------------

interface PlanAlert {
    id: string;
    tone: "info" | "warning" | "critical";
    title: string;
    body: string;
    action?: "pay" | "upgrade";
}

function buildAlerts(plan: PlanResponse, usage: PlanUsage): PlanAlert[] {
    const alerts: PlanAlert[] = [];
    const storage = usageLevel(usage.storage.usedBytes, usage.storage.limitBytes);
    const users = usageLevel(usage.users.used, usage.users.limit);
    const canRequest = !plan.pendingUpgrade;

    if (plan.payment.status === "UNPAID") {
        alerts.push({
            id: "unpaid",
            tone: "info",
            title: "Your plan isn't active yet",
            body: "Pay for your plan to start uploading assets and adding users.",
            action: "pay",
        });
    }

    if (storage === "full") {
        alerts.push({
            id: "storage-full",
            tone: "critical",
            title: "Storage is full",
            body: `You've used ${formatBytes(usage.storage.usedBytes)} of ${plan.plan.storageLimitLabel}. New asset uploads are paused until your plan is upgraded.`,
            action: canRequest ? "upgrade" : undefined,
        });
    } else if (storage === "warning") {
        alerts.push({
            id: "storage-warning",
            tone: "warning",
            title: `Storage is ${Math.round(usage.storage.pct)}% full`,
            body: `${formatBytes(Math.max(0, usage.storage.limitBytes - usage.storage.usedBytes))} left. Uploads stop when storage reaches 100%.`,
            action: canRequest ? "upgrade" : undefined,
        });
    }

    if (users === "full") {
        alerts.push({
            id: "users-full",
            tone: "critical",
            title: "User limit reached",
            body: `All ${usage.users.limit} user seats are in use. No more users can be created on this plan.`,
            action: canRequest ? "upgrade" : undefined,
        });
    } else if (users === "warning") {
        alerts.push({
            id: "users-warning",
            tone: "warning",
            title: `${usage.users.used} of ${usage.users.limit} user seats in use`,
            body: `${usage.users.limit - usage.users.used} ${usage.users.limit - usage.users.used === 1 ? "seat" : "seats"} left before no more users can be created.`,
            action: canRequest ? "upgrade" : undefined,
        });
    }

    if (plan.pendingUpgrade) {
        const p = plan.pendingUpgrade;
        alerts.push({
            id: "pending",
            tone: "info",
            title: `Upgrade request ${p.ticketReference} sent`,
            body: `You asked for ${p.userLimit} users and ${formatBytes(p.storageLimitGb * 1024 ** 3)} on ${formatDate(p.requestedAt)}. Your plan updates once your commercial manager approves it.`,
        });
    }

    return alerts;
}

const TONE: Record<PlanAlert["tone"], { box: string; icon: React.ReactNode; button: string }> = {
    info: {
        box: "border-blue-200 bg-blue-50",
        icon: <Info size={20} className="text-blue-600" />,
        button: "bg-blue-600 hover:bg-blue-700",
    },
    warning: {
        box: "border-amber-200 bg-amber-50",
        icon: <AlertTriangle size={20} className="text-amber-600" />,
        button: "bg-amber-600 hover:bg-amber-700",
    },
    critical: {
        box: "border-red-200 bg-red-50",
        icon: <XCircle size={20} className="text-red-600" />,
        button: "bg-red-600 hover:bg-red-700",
    },
};

// ---- usage meter ----------------------------------------------------------------

const BAR: Record<UsageLevel, string> = { ok: "bg-blue-600", warning: "bg-amber-500", full: "bg-red-600" };
const READOUT: Record<UsageLevel, string> = { ok: "text-gray-900", warning: "text-amber-700", full: "text-red-700" };

const UsageMeter: React.FC<{
    icon: React.ReactNode;
    title: string;
    used: string;
    limit: string;
    pct: number;
    level: UsageLevel;
    footnote: string;
}> = ({ icon, title, used, limit, pct, level, footnote }) => (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-2 text-sm text-gray-600">
            {icon}
            {title}
        </div>
        <div className="mt-3 flex items-baseline justify-between gap-3">
            <p className={`text-2xl font-semibold ${READOUT[level]}`}>
                {used} <span className="text-base font-normal text-gray-500">of {limit}</span>
            </p>
            <p className={`text-sm font-medium ${READOUT[level]}`}>{Math.round(pct)}%</p>
        </div>
        <div
            className="relative mt-3 h-3 overflow-hidden rounded-full bg-gray-100"
            role="progressbar"
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${title} used`}
        >
            <div className={`h-full rounded-full transition-[width] duration-500 ${BAR[level]}`} style={{ width: `${Math.min(100, pct)}%` }} />
            {/* 90% alert threshold */}
            <div className="absolute top-0 h-full w-px bg-gray-400/70" style={{ left: `${WARN_PCT}%` }} />
        </div>
        <p className="mt-2 text-sm text-gray-500">{footnote}</p>
    </div>
);

// ---- page -------------------------------------------------------------------------

const CommercialManagerDashboard: React.FC<CommercialManagerProps> = () => {
    const { plan, setPlan, loading, error, refresh } = usePlan();
    const [scenario, setScenario] = useState<ScenarioId>(getActiveScenario);
    const [payOpen, setPayOpen] = useState(false);
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [resetting, setResetting] = useState(false);

    const pickScenario = (id: ScenarioId) => {
        setActiveScenario(id);
        setScenario(id);
    };

    const handleReset = async () => {
        if (!window.confirm("Reset payment for this organisation? Invoices and any pending upgrade request are cleared too.")) return;
        setResetting(true);
        try {
            setPlan(await resetDemoPayment());
        } catch (e) {
            window.alert(e instanceof Error ? e.message : "Reset failed");
        } finally {
            setResetting(false);
        }
    };

    if (loading && !plan) {
        return <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">Loading your plan</div>;
    }

    if (error || !plan) {
        return (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6">
                <p className="font-medium text-red-800">Couldn't load billing details</p>
                <p className="mt-1 text-sm text-red-700">{error}</p>
                <button onClick={refresh} className="mt-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
                    Try again
                </button>
            </div>
        );
    }

    const usage = applyScenario(plan.usage, scenario);
    const storageLevel = usageLevel(usage.storage.usedBytes, usage.storage.limitBytes);
    const usersLevel = usageLevel(usage.users.used, usage.users.limit);
    const alerts = buildAlerts(plan, usage);
    const paid = plan.payment.status === "PAID";
    const price = plan.plan.price;

    const seatsLeft = Math.max(0, usage.users.limit - usage.users.used);
    const bytesLeft = Math.max(0, usage.storage.limitBytes - usage.storage.usedBytes);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Financial &amp; Billing</h1>
                    <p className="mt-1 text-sm text-gray-500">{plan.tenant.name}</p>
                </div>
                <button onClick={refresh} className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <RefreshCw size={16} className={loading ? "animate-spin" : undefined} /> Refresh
                </button>
            </div>

            {/* Demo scenario switcher */}
            <section className="rounded-xl border border-dashed border-indigo-300 bg-indigo-50/60 p-4" aria-labelledby="demo-heading">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 id="demo-heading" className="text-sm font-semibold text-indigo-900">Demo preview</h2>
                        <p className="mt-0.5 text-sm text-indigo-800/80">
                            See how this page looks at different usage levels. Payment status always shows real data.
                        </p>
                    </div>
                    <button
                        onClick={handleReset}
                        disabled={resetting}
                        className="flex items-center gap-1.5 text-sm text-indigo-800 underline-offset-2 hover:underline disabled:opacity-50"
                    >
                        <RotateCcw size={14} /> Reset demo payment
                    </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Usage scenario">
                    {SCENARIOS.map((s) => {
                        const active = s.id === scenario;
                        return (
                            <button
                                key={s.id}
                                role="radio"
                                aria-checked={active}
                                onClick={() => pickScenario(s.id)}
                                className={`rounded-full border px-3 py-1.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
                                    active
                                        ? "border-indigo-600 bg-indigo-600 text-white"
                                        : "border-indigo-200 bg-white text-indigo-900 hover:border-indigo-400"
                                }`}
                            >
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Alerts */}
            {alerts.length > 0 && (
                <div className="space-y-3">
                    {alerts.map((a) => (
                        <div key={a.id} className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-5 py-4 ${TONE[a.tone].box}`} role={a.tone === "critical" ? "alert" : "status"}>
                            <div className="flex items-start gap-3">
                                <span className="mt-0.5 shrink-0">{TONE[a.tone].icon}</span>
                                <div>
                                    <p className="font-medium text-gray-900">{a.title}</p>
                                    <p className="mt-0.5 text-sm text-gray-700">{a.body}</p>
                                </div>
                            </div>
                            {a.action === "pay" && (
                                <button onClick={() => setPayOpen(true)} className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${TONE[a.tone].button}`}>
                                    Pay now
                                </button>
                            )}
                            {a.action === "upgrade" && (
                                <button onClick={() => setUpgradeOpen(true)} className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${TONE[a.tone].button}`}>
                                    Request upgrade
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Plan + usage */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                    <div className="flex items-center justify-between">
                        <h2 className="font-semibold text-gray-900">Your plan</h2>
                        {paid ? (
                            <span className="flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                <CheckCircle2 size={12} /> Active
                            </span>
                        ) : (
                            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">Awaiting payment</span>
                        )}
                    </div>

                    <p className="mt-4 text-3xl font-semibold text-gray-900">
                        {formatMoney(price.total, price.currency)}
                        <span className="text-base font-normal text-gray-500"> / month</span>
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                        {plan.plan.userLimit} users and {plan.plan.storageLimitLabel} storage{price.tax > 0 ? ", tax included" : ""}
                    </p>

                    <dl className="mt-4 space-y-1.5 border-t border-gray-100 pt-4 text-sm">
                        {price.lineItems.map((li) => (
                            <div key={li.code} className="flex justify-between">
                                <dt className="text-gray-600">{li.description}</dt>
                                <dd className="text-gray-900">{formatMoney(li.amount, price.currency)}</dd>
                            </div>
                        ))}
                        {price.tax > 0 && (
                            <div className="flex justify-between">
                                <dt className="text-gray-600">Tax ({Math.round(price.taxRate * 100)}%)</dt>
                                <dd className="text-gray-900">{formatMoney(price.tax, price.currency)}</dd>
                            </div>
                        )}
                    </dl>

                    {paid && (
                        <dl className="mt-4 space-y-1.5 border-t border-gray-100 pt-4 text-sm">
                            <div className="flex justify-between">
                                <dt className="text-gray-600">Current period</dt>
                                <dd className="text-gray-900">
                                    {formatDate(plan.payment.periodStart)} to {formatDate(plan.payment.periodEnd)}
                                </dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-gray-600">Next invoice</dt>
                                <dd className="text-gray-900">{formatDate(plan.payment.nextBillingDate)}</dd>
                            </div>
                        </dl>
                    )}

                    <p className="mt-4 text-xs text-gray-500">Your plan is set by SiMB and can't be edited here.</p>

                    <div className="mt-4 flex flex-col gap-2">
                        {!paid && (
                            <button onClick={() => setPayOpen(true)} className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 py-2 text-sm font-medium text-white hover:bg-blue-700">
                                <CreditCard size={16} /> Pay now
                            </button>
                        )}
                    </div>
                </div>

                <div className="space-y-6 lg:col-span-2">
                    {scenario !== "live" && (
                        <p className="text-sm text-indigo-800">
                            Showing simulated usage for “{scenarioLabel(scenario)}”.
                        </p>
                    )}
                    <UsageMeter
                        icon={<HardDrive size={16} />}
                        title="Storage"
                        used={formatBytes(usage.storage.usedBytes)}
                        limit={plan.plan.storageLimitLabel}
                        pct={usage.storage.pct}
                        level={storageLevel}
                        footnote={
                            storageLevel === "full"
                                ? "No space left. Asset uploads are paused."
                                : `${formatBytes(bytesLeft)} left. You'll get an alert at ${WARN_PCT}%.`
                        }
                    />
                    <UsageMeter
                        icon={<Users size={16} />}
                        title="Users"
                        used={String(usage.users.used)}
                        limit={`${usage.users.limit} users`}
                        pct={usage.users.pct}
                        level={usersLevel}
                        footnote={
                            usersLevel === "full"
                                ? "No seats left. New users can't be created."
                                : `${seatsLeft} ${seatsLeft === 1 ? "seat" : "seats"} left. Only active users count.`
                        }
                    />
                </div>
            </div>

            {/* Invoices */}
            <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="border-b border-gray-200 px-5 py-4">
                    <h2 className="font-semibold text-gray-900">Invoices</h2>
                </div>
                {plan.invoices.length === 0 ? (
                    <div className="px-5 py-10 text-center">
                        <p className="text-gray-900">No invoices yet</p>
                        <p className="mt-1 text-sm text-gray-500">Your first invoice appears here once the plan is paid.</p>
                        {!paid && (
                            <button onClick={() => setPayOpen(true)} className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                                Pay now
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-left text-gray-600">
                                <tr>
                                    <th className="px-5 py-3 font-medium">Invoice</th>
                                    <th className="px-5 py-3 font-medium">Service period</th>
                                    <th className="px-5 py-3 font-medium">Plan</th>
                                    <th className="px-5 py-3 font-medium">Paid on</th>
                                    <th className="px-5 py-3 text-right font-medium">Amount</th>
                                    <th className="px-5 py-3 font-medium">Status</th>
                                    <th className="px-5 py-3"><span className="sr-only">Download</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {plan.invoices.map((inv) => (
                                    <tr key={inv.id} className="border-t border-gray-100">
                                        <td className="px-5 py-3 font-medium text-gray-900">{inv.invoiceNumber}</td>
                                        <td className="px-5 py-3 text-gray-700">
                                            {formatDate(inv.periodStart)} to {formatDate(inv.periodEnd)}
                                        </td>
                                        <td className="px-5 py-3 text-gray-700">
                                            {inv.userLimit} users, {inv.storageLimitLabel || `${inv.storageLimitGb} GB`}
                                        </td>
                                        <td className="px-5 py-3 text-gray-700">{formatDate(inv.paidAt)}</td>
                                        <td className="px-5 py-3 text-right text-gray-900">{formatMoney(inv.total, inv.currency)}</td>
                                        <td className="px-5 py-3">
                                            <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">Paid</span>
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <button
                                                onClick={() => {
                                                    if (!openInvoicePrintView(inv, plan.tenant.name)) window.alert("Allow pop-ups for this site to download the invoice.");
                                                }}
                                                className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800"
                                            >
                                                <Download size={14} /> Download
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <PaymentModal open={payOpen} plan={plan} context="billing" onClose={() => setPayOpen(false)} onPaid={setPlan} />
            <UpgradeRequestModal
                open={upgradeOpen}
                plan={plan}
                usage={usage}
                scenarioContext={scenario !== "live" ? scenarioLabel(scenario) : undefined}
                onClose={() => setUpgradeOpen(false)}
                onRequested={setPlan}
            />
        </div>
    );
};

export default CommercialManagerDashboard;